// The one place public pages get ConfirmPH's contact details from.
//
// Order of truth:
//   1. the canonical Task 2 operator record (/api/legal/operator) — support,
//      privacy and security contacts as recorded there;
//   2. the single monitored inbox published before that record existed.
// A placeholder or reserved address (example.com, .test, .invalid, …) is
// never published as a contact, even if one ends up in the data: showing a
// fake DPO address is worse than showing none.
import { useEffect, useState } from "react";

import { SUPPORT_EMAIL } from "@/content/legal";

const RESERVED = /@(?:[a-z0-9-]+\.)*(example\.(?:com|org|net)|test|invalid|localhost)$|@example$/i;

export function isPublishableContact(value) {
  if (!value || typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (v.includes("@") && RESERVED.test(v)) return false;
  return !/placeholder|\[.*\]/i.test(v);
}

let cached = null;
let inflight = null;

function load() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = import("@/lib/api")
      .then(({ api }) => api.get("/legal/operator"))
      .then((r) => { cached = r.data; return cached; })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function resolveContacts(operator) {
  const support = isPublishableContact(operator?.support_email) ? operator.support_email : SUPPORT_EMAIL;
  const privacyFromRecord = operator?.privacy?.contact || operator?.privacy_contact;
  const privacy = isPublishableContact(privacyFromRecord) ? privacyFromRecord : support;
  const dpoAppointed = Boolean(operator?.privacy?.dpo_appointed && operator?.privacy?.dpo_name);
  return {
    operatorName: operator?.operator_name || "ConfirmPH",
    disclosure: operator?.disclosure || null,
    legalEntityRecorded: Boolean(operator?.legal_entity_recorded),
    support,
    privacy,
    // Security reports go to the security contact once one is configured;
    // until then, the monitored support inbox.
    security: isPublishableContact(operator?.security_contact) ? operator.security_contact : support,
    dpoAppointed,
    dpoName: dpoAppointed ? operator.privacy.dpo_name : null,
  };
}

export function useOperatorContacts() {
  const [operator, setOperator] = useState(cached);
  useEffect(() => {
    let alive = true;
    load().then((o) => { if (alive && o) setOperator(o); });
    return () => { alive = false; };
  }, []);
  return resolveContacts(operator);
}
